import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';

// ─── Dispute History Panel ─────────────────────────────────────────────────────
export function DisputeHistoryPanel({ locationId }) {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [err, setErr] = React.useState('');
  const [filterStatus, setFilterStatus] = React.useState('all');
  const [filterFrom, setFilterFrom] = React.useState('');
  const [filterTo, setFilterTo] = React.useState('');

  // D104: the catch used to discard the error and just clear the loading flag,
  // so `if (!data) return null` rendered the panel as nothing at all. Not even
  // a spinner — the section simply was not there, so there was nothing to
  // report. An admin could not tell "no disputes" from "this never loaded".
  function load() {
    setLoading(true);
    setErr('');
    api('GET', `/api/disputes/history?location_id=${locationId}`)
      .then(d => { setData(d); setLoading(false); })
      .catch(e => {
        let m = (e && e.message) || String(e);
        try { m = JSON.parse(m).error || m; } catch { /* not JSON */ }
        setErr(m);
        setLoading(false);
      });
  }

  React.useEffect(load, [locationId]);

  if (loading) return <div className="text-center py-8 text-gray-400">Loading...</div>;
  if (err) {
    return (
      <div className="card" style={{ borderLeft: '4px solid #c62828' }}>
        <div className="text-sm" style={{ fontWeight: 600, marginBottom: 4 }}>Couldn’t load dispute history</div>
        <div className="text-xs text-red-700" style={{ marginBottom: 10 }}>{err}</div>
        <button className="btn-secondary text-xs" onClick={load}>Try again</button>
      </div>
    );
  }
  if (!data) return null;

  const { disputes, stats } = data;
  let filtered = disputes;
  if (filterStatus !== 'all') filtered = filtered.filter(d => d.status === filterStatus);
  if (filterFrom) filtered = filtered.filter(d => d.flagged_at >= filterFrom);
  if (filterTo) filtered = filtered.filter(d => d.flagged_at <= filterTo + 'T23:59:59');

  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <div className="bg-white rounded-xl border border-gray-200 p-4 text-center shadow-sm">
          <p className="text-2xl font-bold text-ink">{stats.total}</p>
          <p className="text-xs text-gray-500 mt-0.5">Total Disputes</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4 text-center shadow-sm">
          <p className="text-2xl font-bold text-ink">{stats.this_month}</p>
          <p className="text-xs text-gray-500 mt-0.5">This Month</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4 col-span-2 shadow-sm">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Top Flagged Tasks</p>
          {stats.top_tasks.length === 0 ? <p className="text-xs text-gray-400">None yet</p> : (
            <div className="space-y-1">
              {stats.top_tasks.slice(0,3).map((t,i) => (
                <div key={i} className="flex items-center justify-between text-xs">
                  <span className="text-gray-700 truncate">{t.title}</span>
                  <span className="text-crimson font-semibold ml-2 flex-shrink-0">{t.count}×</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-4 items-center">
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm text-ink bg-white focus:outline-none">
          <option value="all">All statuses</option>
          <option value="open">Open</option>
          <option value="resolved">Resolved</option>
        </select>
        <input type="date" value={filterFrom} onChange={e => setFilterFrom(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm text-ink bg-white focus:outline-none"/>
        <span className="text-gray-400 text-xs">to</span>
        <input type="date" value={filterTo} onChange={e => setFilterTo(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm text-ink bg-white focus:outline-none"/>
        {(filterStatus !== 'all' || filterFrom || filterTo) && (
          <button onClick={() => { setFilterStatus('all'); setFilterFrom(''); setFilterTo(''); }}
            className="text-xs text-gray-400 hover:text-gray-600 underline">Clear</button>
        )}
        <span className="text-xs text-gray-400 ml-auto">{filtered.length} result{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-12 text-gray-400"><p className="text-3xl mb-2">🏁</p><p className="text-sm">No disputes match the filters</p></div>
      ) : (
        <div className="space-y-3">
          {filtered.map(flag => (
            <div key={flag.id} className={`rounded-xl border p-4 ${flag.status==='open'?'border-red-200 bg-red-50':'border-gray-200 bg-white'}`}>
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="text-sm font-bold text-ink">{flag.task_title}</span>
                    {flag.status==='open'
                      ? <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-semibold">🚩 Open</span>
                      : <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-semibold">✓ Resolved</span>
                    }
                  </div>
                  <p className="text-sm text-gray-700 mb-2">{flag.description}</p>
                  <div className="text-xs text-gray-400 space-y-0.5">
                    <p>Reported by <span className="text-ink font-medium">{flag.flagged_by_name}</span> · {new Date(flag.flagged_at).toLocaleDateString()}</p>
                    {flag.completed_by_name && <p>Completed by <span className="text-amber-600 font-medium">{flag.completed_by_name}</span></p>}
                    {flag.resolved_by_name && <p>Resolved by <span className="text-green-600 font-medium">{flag.resolved_by_name}</span>{flag.resolved_at ? ` · ${new Date(flag.resolved_at).toLocaleDateString()}` : ''}</p>}
                  </div>
                  {flag.photo_url && (
                    <img src={flag.photo_url} alt="Evidence" className="mt-2 w-20 h-16 object-cover rounded-lg border border-gray-200 cursor-pointer"
                      onClick={() => window.open(flag.photo_url)}/>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
